/**
 * Iter273.7 — Beta-tester #24: "Quick reference panel on the
 * results screen" that explains what each tier on the Quantum
 * Exposure scale actually means.
 *
 * Testers consistently asked "is a high number good or bad?" and
 * "what's the difference between Moderate and High?". The colorblind
 * icons (iter273.3.c) gave a shape per tier, and the direction caption
 * (iter273.7) clarifies the polarity, but neither tells you what to DO
 * once you see a number. This legend bridges that gap.
 *
 * Rendered inline beneath the QuantumScoreDial on the scan-result
 * page. Compact 4-row vertical legend that shares the same tier
 * iconography + colour palette as the dial — so a tester eyeballing
 * the dial can map "octagon red" → "Critical · migrate now" in one
 * glance.
 */
import React from "react";
import { AlertOctagon, AlertTriangle, AlertCircle, ShieldCheck } from "lucide-react";

const TIERS = [
  {
    name:        "Critical",
    range:       "80–100",
    color:       "#FF2A2A",
    Icon:        AlertOctagon,
    headline:    "Migrate before next signing",
    detail:      "Public key on-chain + active spending. Shor-vulnerable on Q-Day.",
  },
  {
    name:        "High",
    range:       "60–79",
    color:       "#FF8000",
    Icon:        AlertTriangle,
    headline:    "Migrate this quarter",
    detail:      "Material balance, P2PK or reused address. Harvest-now risk material.",
  },
  {
    name:        "Moderate",
    range:       "30–59",
    color:       "#FFD60A",
    Icon:        AlertCircle,
    headline:    "Monitor + plan migration",
    detail:      "Modern address type, but legacy or supply-chain exposure remains.",
  },
  {
    name:        "PQC Ready",
    range:       "0–29",
    color:       "#1FDD83",
    Icon:        ShieldCheck,
    headline:    "Maintain hygiene",
    detail:      "PQC-aligned or low-exposure pattern. Re-scan periodically.",
  },
];


export default function ScoreScaleLegend({ activeTier }) {
  return (
    <div
      className="qt-card"
      data-testid="score-scale-legend"
      style={{
        padding: 16,
        background: "rgba(4,9,20,0.55)",
        borderColor: "var(--qt-border-soft)",
      }}
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="qt-label" style={{ color: "var(--qt-text-3)" }}>
          WHAT DOES THIS MEAN?
        </div>
        <div className="qt-mono text-[10px]"
             style={{ color: "var(--qt-text-3)", letterSpacing: "0.14em" }}>
          ↑ HIGHER = MORE EXPOSED
        </div>
      </div>
      <ul className="space-y-2" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {TIERS.map((t) => {
          const isActive = activeTier
            && t.name.toLowerCase().replace(/\s+/g, "")
               === activeTier.toLowerCase().replace(/\s+/g, "");
          return (
            <li
              key={t.name}
              data-testid={`score-scale-row-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              data-active={isActive ? "true" : "false"}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr auto",
                gap: 12,
                alignItems: "start",
                padding: "8px 10px",
                borderRadius: 8,
                background: isActive ? `${t.color}10` : "transparent",
                border: isActive
                  ? `1px solid ${t.color}66`
                  : "1px solid var(--qt-border-soft)",
                transition: "all 0.15s",
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 28, height: 28, borderRadius: 6,
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  background: `${t.color}20`,
                  color: t.color,
                  border: `1px solid ${t.color}66`,
                  flexShrink: 0,
                }}
              >
                <t.Icon size={14} strokeWidth={2.25} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div className="qt-mono"
                     style={{ fontSize: 12, color: t.color,
                              letterSpacing: "0.14em", fontWeight: 700,
                              textTransform: "uppercase" }}>
                  {t.name === "PQC Ready" ? "PQC READY" : t.name.toUpperCase()}
                  <span className="qt-mono text-[10px] ml-2"
                        style={{ color: "var(--qt-text-3)",
                                 letterSpacing: "0.06em", fontWeight: 500 }}>
                    score {t.range}
                  </span>
                </div>
                <div className="text-[13px] mt-0.5"
                     style={{ color: "#fff", lineHeight: 1.4, fontWeight: 600 }}>
                  {t.headline}
                </div>
                <div className="text-[12px] mt-0.5"
                     style={{ color: "var(--qt-text-3)", lineHeight: 1.45 }}>
                  {t.detail}
                </div>
              </div>
              {isActive && (
                <span
                  className="qt-mono text-[9px]"
                  data-testid="score-scale-active-marker"
                  style={{
                    color: t.color,
                    letterSpacing: "0.18em",
                    padding: "2px 6px",
                    border: `1px solid ${t.color}88`,
                    borderRadius: 999,
                    background: `${t.color}1A`,
                    flexShrink: 0,
                    alignSelf: "center",
                  }}
                >
                  YOU
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <div className="qt-mono text-[10px] mt-3"
           style={{ color: "var(--qt-text-3)" }}>
        Full scoring rubric: <a href="/methodology"
                                style={{ color: "var(--qt-cyan)",
                                         textDecoration: "underline" }}>
          /methodology §05
        </a>
      </div>
    </div>
  );
}
